import {
  clearStatusByGeometryTask,
  GeometryTask,
  GeometryTaskType,
  MaterialDownloadTask,
  removeMaterialDownloadTask,
  selectMaterialDownloadTasks,
  selectTasks,
  TaskSource,
  useAppDispatch,
} from "@org-avp/avp-avengers-ui-shared-store";
import { MouseEventHandler, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";

export interface TaskStatus {
  id: string;
  descriptorTKey: string;
  startedAt: Date;
  total: number;
  success: number;
  failed: number;
  paused?: boolean;
  sources: TaskStatusSource[];
  onPause?: MouseEventHandler;
  onResume?: MouseEventHandler;
  onCancel?: MouseEventHandler;
}

export enum TaskStatusSource {
  PENDING_GEOMETRIES,
  MATERIAL_DOWNLOAD,
}

export interface TasksCollectorHelper {
  tasks: TaskStatus[];
  hideTask: (task: TaskStatus) => void;
}

export function useTasksCollectorHelper() {
  const appDispatch = useAppDispatch();
  const hideTask = useCallback<TasksCollectorHelper["hideTask"]>(
    (task) => {
      if (task.sources.includes(TaskStatusSource.PENDING_GEOMETRIES)) appDispatch(clearStatusByGeometryTask(task.id));
      if (task.sources.includes(TaskStatusSource.MATERIAL_DOWNLOAD)) appDispatch(removeMaterialDownloadTask(task.id));
    },
    [appDispatch],
  );

  const geometryTasks = useSelector(selectTasks);
  const materialDownloadTasks = useSelector(selectMaterialDownloadTasks);

  const geometryTaskStatuses: TaskStatus[] = useMemo(
    () => geometryTasks
      .filter((task) => task.source !== TaskSource.PLMXML) // filter out PLMXML tasks, as they are handled separately
      .map((task) => {
        const total = geometryTaskGetTotal(task);
        const success = geometryTaskGetSuccessful(task);
        const failed = geometryTaskGetFailed(task);
        return ({
          id: task.taskId,
          descriptorTKey: getDescriptorTKey(task),
          startedAt: new Date(task.createdAt),
          total: total,
          success: success,
          failed: failed,
          completed: total === success + failed,
          sources: [TaskStatusSource.PENDING_GEOMETRIES],
        });
      }),
    [geometryTasks],
  );

  const plmXmlTaskStatuses = useMemo(
    () => {
      const plmxmlGeometryTaskStatuses = geometryTasks.filter((task) => task.source === TaskSource.PLMXML);
      const geometryAndMaterialPlmxmlExportTasks = plmxmlGeometryTaskStatuses
        .map((geometryTask) => {
          const materialDownloadTask = materialDownloadTasks.taskLookup[geometryTask.taskId];
          return materialDownloadTask ? [geometryTask, materialDownloadTask] : undefined;
        })
        .filter((arr): arr is [GeometryTask, MaterialDownloadTask] => arr != null)
        .map(([geometryTask, materialDownloadTask]): TaskStatus => ({
          id: geometryTask.taskId,
          descriptorTKey: "titleBar.taskDisplay.taskDescriptor.plmxmlExport",
          startedAt: new Date(geometryTask.createdAt < materialDownloadTask.createdAt ? geometryTask.createdAt : materialDownloadTask.createdAt),
          total: geometryTaskGetTotal(geometryTask) + materialDownloadTask.total,
          success: geometryTaskGetSuccessful(geometryTask) + materialDownloadTask.success,
          failed: geometryTaskGetFailed(geometryTask) + materialDownloadTask.failed,
          sources: [TaskStatusSource.PENDING_GEOMETRIES, TaskStatusSource.MATERIAL_DOWNLOAD],
        }));
      const geometryOnlyPlmxmlTasks = plmxmlGeometryTaskStatuses
        .filter((geometryTask) => !geometryAndMaterialPlmxmlExportTasks.some((taskStatus) => taskStatus.id === geometryTask.taskId))
        .map((geometryTask): TaskStatus => ({
          id: geometryTask.taskId,
          descriptorTKey: "titleBar.taskDisplay.taskDescriptor.plmxmlExport",
          startedAt: new Date(geometryTask.createdAt),
          total: geometryTaskGetTotal(geometryTask),
          success: geometryTaskGetSuccessful(geometryTask),
          failed: geometryTaskGetFailed(geometryTask),
          sources: [TaskStatusSource.PENDING_GEOMETRIES],
        }));
      const materialOnlyPlmxmlTasks = Object.values(materialDownloadTasks.taskLookup)
        .filter((materialDownloadTask) => !geometryAndMaterialPlmxmlExportTasks.some((taskStatus) => taskStatus.id === materialDownloadTask.taskId))
        .map((materialDownloadTask): TaskStatus => ({
          id: materialDownloadTask.taskId,
          descriptorTKey: "titleBar.taskDisplay.taskDescriptor.plmxmlExport",
          startedAt: new Date(materialDownloadTask.createdAt),
          total: materialDownloadTask.total,
          success: materialDownloadTask.success,
          failed: materialDownloadTask.failed,
          sources: [TaskStatusSource.MATERIAL_DOWNLOAD],
        }));

      return [
        ...geometryAndMaterialPlmxmlExportTasks,
        ...geometryOnlyPlmxmlTasks,
        ...materialOnlyPlmxmlTasks,
      ];
    },
    [geometryTasks, materialDownloadTasks.taskLookup],
  );

  const tasks = useMemo(
    () => [...geometryTaskStatuses, ...plmXmlTaskStatuses]
      .sort((a, b) => a.startedAt.getTime() - b.startedAt.getTime()),
    [geometryTaskStatuses, plmXmlTaskStatuses],
  );

  return useMemo(
    () => ({
      tasks,
      hideTask,
    }),
    [tasks, hideTask],
  );
}

function getDescriptorTKey(task: GeometryTask): string {
  switch (task.source) {
    case TaskSource.KVS:
      return "titleBar.taskDisplay.taskDescriptor.kvsDownload";
    case TaskSource.GEOMETRY:
      switch (task.taskType) {
        case GeometryTaskType.GEOMETRY_DOWNLOAD:
          return "titleBar.taskDisplay.taskDescriptor.geometryDownload";
        case GeometryTaskType.GEOMETRY_UPLOAD:
          return "titleBar.taskDisplay.taskDescriptor.geometryUpload";
        default:
          throw new Error("Unknown task type");
      }
    case TaskSource.PLMXML:
      return "titleBar.taskDisplay.taskDescriptor.plmxmlExport";
  }
}

function geometryTaskGetTotal(task: GeometryTask) {
  return Object.entries(task.status).reduce(
    (total, [, fileStatusByFileName]) => total + Object.keys(fileStatusByFileName).length,
    0,
  );
}

function geometryTaskGetSuccessful(task: GeometryTask) {
  return Object.entries(task.status).reduce(
    (successful, [, fileStatusByFileName]) =>
      successful + Object.values(fileStatusByFileName).filter((fileStatus) => fileStatus.status === "SUCCESS").length,
    0,
  );
}

function geometryTaskGetFailed(task: GeometryTask) {
  return Object.entries(task.status).reduce(
    (failed, [, fileStatusByFileName]) =>
      failed + Object.values(fileStatusByFileName).filter((fileStatus) => fileStatus.status === "ERROR").length,
    0,
  );
}
